import React, { useEffect, useRef } from "react";
import {
  Button,
  Checkbox,
  Input,
  LoginWrapper,
  PhoneWithPrefix,
  SelectComponent,
} from "shared";
import "./RegisterPage.scss";
import { Field, Formik, Form } from "formik";
import { ROUTE_ENUM, VALIDATION_ENUM } from "enums";
import * as Yup from "yup";
import { useLocation, useNavigate } from "react-router";
import { useGetPhonePrefixes } from "hooks/phone";
import { useCreateAccount } from "hooks/users";
import { useGetSmsCode } from "hooks/auth";
import { validatePhoneNumber } from "utils/validation/phoneValidation";
import { isDesktop } from "utils";

type RegisterPageProps = {};

const RegisterPage: React.FC<RegisterPageProps> = () => {
  const { data: phonePrefixes } = useGetPhonePrefixes();
  const navigate = useNavigate();
  const location = useLocation();
  const handleRegulationClick = (route: ROUTE_ENUM) => {
    if (isDesktop()) {
      window.open(route, "_blank", "noopener,noreferrer");
    } else {
      if (formRef.current) {
        navigate(
          { pathname: location.pathname, search: location.search },
          { replace: true, state: { registerFormValues: formRef.current.values } }
        );
      }
      navigate(route);
    }
  };
  const formRef = useRef<any>(null);
  const prefix = phonePrefixes?.find(
    (prefix) => prefix.value === formRef.current?.values.phonePrefixUuid
  );
  const { mutateAsync: createAccount } = useCreateAccount({
    onError: (error: Error) => {
      console.error("Błąd podczas rejestracji:", error.message);
    },
  });

  const { mutate: getSmsCode } = useGetSmsCode({
    onSuccess: () => {
      navigate(ROUTE_ENUM.ENTER_CODE, {
        state: {
          phoneNumber: ` ${prefix?.label} ${formRef.current.values.phoneNumber}`,
          phonePrefixUuid: formRef.current.values.phonePrefixUuid,
          phone: `${formRef.current.values.phoneNumber}`,
        },
      });
    },
    onError: (error: Error) => {
      console.error("Błąd podczas rejestracji:", error.message);
    },
  });

  const validation = () =>
    Yup.object().shape({
      name: Yup.string()
        .required(VALIDATION_ENUM.REQUIRED)
        .matches(
          /^[^\s].*?[^\s]$/,
          VALIDATION_ENUM.CANNOT_START_AND_END_WITH_SPACE
        ),
      phonePrefixUuid: Yup.string().required(VALIDATION_ENUM.REQUIRED),
      phoneNumber: Yup.string()
        .required(VALIDATION_ENUM.REQUIRED)
        .test("phone-validation", VALIDATION_ENUM.NUMBER, function (value) {
          if (!/^\d+$/.test(value)) {
            return false;
          }

          const { phonePrefixUuid } = this.parent;

          if (value && phonePrefixUuid && phonePrefixes?.length) {
            const validation = validatePhoneNumber(
              value,
              phonePrefixUuid,
              phonePrefixes
            );

            if (!validation.isValid) {
              return this.createError({
                message: validation.error || VALIDATION_ENUM.NUMBER,
              });
            }
          }

          return true;
        }),
      acceptance: Yup.boolean().oneOf([true], VALIDATION_ENUM.REQUIRED),
      rodo: Yup.boolean().oneOf([true], VALIDATION_ENUM.REQUIRED),
    });

  const initialValues = {
    name: "",
    phonePrefixUuid: "",
    phoneNumber: "",
    acceptance: false,
    rodo: false,
    ...((location.state as { registerFormValues?: object } | null)
      ?.registerFormValues ?? {}),
  };

  const onSubmit = async (values: typeof initialValues) => {
    const referrerCode = sessionStorage.getItem("referrerCode") || undefined;

    const payload = {
      ...values,
      ...(referrerCode ? { referrerCode } : {}),
    };

    const register = await createAccount(payload);
    if (register) {
      getSmsCode({ ...payload, isSilent: true });
    }
  };

  useEffect(() => {
    if (phonePrefixes.length && formRef.current) {
      const pl = phonePrefixes.find((prefix) => prefix.label === "+48");
      formRef.current.setFieldValue("phonePrefixUuid", pl?.value);
    }
  }, [phonePrefixes]);

  return (
    <LoginWrapper>
      <h1 className="login-wrapper__header">Utwórz konto</h1>
      <Formik
        initialValues={initialValues}
        onSubmit={(values) => onSubmit(values)}
        validationSchema={validation()}
        innerRef={formRef}
      >
        {({ errors, values, handleSubmit }) => (
          <Form>
            <Field
              type="text"
              id="name"
              name="name"
              label={"Imię i nazwisko"}
              as={Input}
              maxLength={32}
            />
            <PhoneWithPrefix>
              <Field
                label="Telefon"
                name="phonePrefixUuid"
                id="phonePrefixUuid"
                as={SelectComponent}
                options={phonePrefixes}
              />
              <Field
                name="phoneNumber"
                type="text"
                id="phoneNumber"
                maxLength={15}
                as={Input}
              />
            </PhoneWithPrefix>
            <Field
              id="acceptance"
              name="acceptance"
              wrapperClassName="login-wrapper__acceptance"
              label={
                <p>
                  Akceptuję postanowienia
                  <span
                    className="login-wrapper__rodo"
                    style={{ cursor: "pointer" }}
                    onClick={() => handleRegulationClick(ROUTE_ENUM.TERMS_OF_SERVICE)}
                  >
                    {" "}
                    Regulaminu Aplikacji{" "}
                  </span>
                  oraz potwierdzam zapoznanie się z
                  <span
                    className="login-wrapper__rodo"
                    style={{ cursor: "pointer" }}
                    onClick={() => handleRegulationClick(ROUTE_ENUM.PRIVACY_POLICY)}
                  >
                    {" "}
                    Polityką Prywatności.
                  </span>
                </p>
              }
              as={Checkbox}
            />

            <Field
              id="rodo"
              name="rodo"
              wrapperClassName="login-wrapper__rodo"
              label={
                <p>
                  Akceptuję warunki{" "}
                  <span
                    className="login-wrapper__rodo"
                    style={{ cursor: "pointer" }}
                    onClick={() => handleRegulationClick(ROUTE_ENUM.RODO)}
                  >
                    Klauzuli RODO
                  </span>
                </p>
              }
              as={Checkbox}
            />

            <Button
              label="Zarejestruj się"
              className="button--black button--rounded login-wrapper__submit button--wide"
              onClick={() => {
                handleSubmit();
              }}
            />
            <p
              className="login-wrapper__anchor"
              onClick={() => navigate(ROUTE_ENUM.LOGIN)}
            >
              Masz już konto? <span> Zaloguj się</span>
            </p>
          </Form>
        )}
      </Formik>
    </LoginWrapper>
  );
};

export default RegisterPage;
